// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import {
  useAllStatusArtifacts,
  fanoutItems,
  fanoutNotice,
  type StatusFanout,
} from './allStatusArtifacts';
import type { ArtifactSummary } from '../types/procedures';

export interface ProcedureStats {
  total: number;
  approved: number;
  inReview: number;
  draft: number;
  /** Retired artifacts. Not a "rejected" state — the catalog has no such status. */
  deprecated: number;
}

export interface DomainBreakdown {
  domain: string;
  label: string;
  count: number;
  pct: number;
}

export interface ProceduresView {
  stats: ProcedureStats;
  domainBreakdown: DomainBreakdown[];
  isLoading: boolean;
  error: string | null;
  /** Set when some statuses failed or were capped: the figures below are a lower bound. */
  notice: string | null;
  /** Refetches the catalog; set only when some statuses failed (a cap won't change on retry). */
  retry: (() => void) | null;
}

// Fixed identity + order, matching the agreed mockup (Cooling/Power/Shared/Other
// always shown in this order, even at zero) — not "whatever order the API
// returns domains in."
const DOMAIN_LABELS: Record<string, string> = {
  cooling: 'Cooling',
  power: 'Power',
  shared: 'Shared',
  other: 'Other',
};
const DOMAIN_ORDER = ['cooling', 'power', 'shared', 'other'] as const;

function bucketDomain(domain: string | undefined): string {
  return domain && domain in DOMAIN_LABELS && domain !== 'other' ? domain : 'other';
}

function deriveDomainBreakdown(items: ArtifactSummary[]): DomainBreakdown[] {
  const counts = new Map<string, number>();
  for (const item of items) {
    const bucket = bucketDomain(item.categories?.domain);
    counts.set(bucket, (counts.get(bucket) ?? 0) + 1);
  }
  const total = items.length;
  return DOMAIN_ORDER.map(domain => {
    const count = counts.get(domain) ?? 0;
    return {
      domain,
      label: DOMAIN_LABELS[domain],
      count,
      pct: total > 0 ? Math.round((count / total) * 100) : 0,
    };
  });
}

/**
 * Procedure catalog stats and domain breakdown for Admin Center's Home
 * dashboard, via `GET /api/v1/sop-factory/artifacts` — global catalog, not
 * site-scoped, matching Home's "All facilities" framing.
 *
 * `total` per stat comes from each status call's own `total` field (accurate
 * regardless of page size); the domain breakdown buckets the fetched
 * `items` directly, capped at `page_size` per status — fine for today's
 * catalog size, but not exhaustive if any one status ever exceeds 200
 * artifacts. Domain buckets are always returned in a fixed order
 * (cooling/power/shared/other), zero-filled, not derived from whatever
 * order the API happens to return domains in.
 */
export function useProcedures(): ProceduresView {
  const query = useAllStatusArtifacts();

  const byStatus: StatusFanout['byStatus'] = query.data?.byStatus ?? {};
  const pages = Object.values(byStatus).flatMap(page => (page ? [page] : []));
  const allItems = fanoutItems(query.data);

  const stats: ProcedureStats = {
    total: pages.reduce((sum, page) => sum + (page.total ?? page.items.length), 0),
    approved: byStatus.approved?.total ?? 0,
    inReview: byStatus.in_review?.total ?? 0,
    draft: byStatus.draft?.total ?? 0,
    deprecated: byStatus.deprecated?.total ?? 0,
  };

  return {
    stats,
    domainBreakdown: deriveDomainBreakdown(allItems),
    isLoading: query.isPending,
    // A failed Retry keeps the old partial data but sets isError; the tiles stay.
    error: query.isError && !query.data ? 'Unable to load procedures.' : null,
    notice: fanoutNotice(query.data),
    retry: query.data?.failedStatuses.length ? () => void query.refetch() : null,
  };
}
